import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { MultiSelect } from "./ui/multi-select";
import { Loader2 } from "lucide-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  getGroupDetails,
  shareFile,
} from "../api/projectManagementApi";
import { toast } from "sonner";

interface FileSharingProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onShare?: (data: ShareData) => void;
  projectId?: string | undefined;
  files?: Array<{
    name: string;
    path: string;
    type: string;
    [key: string]: any;
  }>;
  isDarkMode?: boolean;
}

export interface ShareData {
  recipients: string[];
  accessLevel: "view" | "comment" | "edit";
  message: string;
}

export default function FileSharing({
  open,
  onOpenChange,
  projectId,
  files,
  isDarkMode = false,
}: FileSharingProps) {
  const [selectedRecipients, setSelectedRecipients] = useState<string[]>([]);
  const [message, setMessage] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);

  // Fetch group details
  const {
    data: groupMembers,
    isLoading: isLoadingMembers,
    error: membersError,
  } = useQuery({
    queryKey: ["groupDetails", projectId],
    queryFn: () => getGroupDetails(projectId),
    enabled: open && !!projectId,
  });

  // Share file mutation
  const shareFileMutation = useMutation({
    mutationFn: ({
      filenames,
      recipientIds,
      message,
    }: {
      filenames: string[];
      recipientIds: string[];
      message?: string;
    }) => {
      if (!projectId) throw new Error("Project ID is required");
      return shareFile(projectId, {
        filenames,
        recipientIds,
        ...(message && { message }),
      });
    },
    onSuccess: (data) => {
      toast.success(data.message || "File shared successfully!");
      // Reset form
      setSelectedRecipients([]);
      setMessage("");
      setSelectedFiles([]);
      onOpenChange(false);
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to share file";
      toast.error(errorMessage);
    },
  });

  const handleShare = () => {
    if (selectedFiles.length === 0) {
      toast.error("Please select at least one file to share");
      return;
    }

    if (selectedRecipients.length === 0) {
      toast.error("Please select at least one recipient");
      return;
    }

    const trimmedMessage = message.trim();
    shareFileMutation.mutate({
      filenames: selectedFiles,
      recipientIds: selectedRecipients,
      ...(trimmedMessage && { message: trimmedMessage }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[700px] max-h-[90vh] overflow-y-auto dark:bg-[#161b22] dark:border-[#30363d] dark:text-[#f0f6fc]">
        <DialogHeader>
          <DialogTitle className="dark:text-[#f0f6fc]">Share File</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Recipients Section */}
          <div className="border rounded-lg p-4 bg-blue-50 dark:bg-blue-900/10 dark:border-blue-900/30 space-y-4">
            <h4 className="font-semibold text-gray-900 dark:text-[#f0f6fc]">Recipients</h4>
            {isLoadingMembers ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400 dark:text-gray-600" />
                <span className="ml-2 text-sm text-gray-500 dark:text-[#8b949e]">
                  Loading members...
                </span>
              </div>
            ) : membersError ? (
              <div className="text-center py-8 text-sm text-red-500">
                Failed to load group members
              </div>
            ) : (
              <MultiSelect
                options={
                  groupMembers && groupMembers.length > 0
                    ? groupMembers
                        .filter((member) => !member.isCurrentUser)
                        .map((member) => ({
                          label: `${member.name} (${
                            member.isLeader ? "Leader" : "Member"
                          })${member.email ? ` - ${member.email}` : ""}`,
                          value: member.userId,
                        }))
                    : []
                }
                selected={selectedRecipients}
                onChange={setSelectedRecipients}
                placeholder="Select recipients..."
                disabled={isLoadingMembers}
                isDarkMode={isDarkMode}
              />
            )}
          </div>

          {/* Files/Content Section */}
          <div className="border rounded-lg p-4 bg-white dark:bg-[#0d1117] dark:border-[#30363d] space-y-4">
            <h4 className="font-semibold text-gray-900 dark:text-[#f0f6fc]">Content to Share</h4>
            {!files || files.length === 0 ? (
              <div className="text-center py-8 text-sm text-gray-500 dark:text-[#8b949e]">
                No files to share
              </div>
            ) : (
              <MultiSelect
                options={files
                  .filter((file) => file.type !== "directory")
                  .map((file) => ({
                    label: file.name,
                    value: file.path,
                  }))}
                selected={selectedFiles}
                onChange={setSelectedFiles}
                placeholder="Select files..."
                isDarkMode={isDarkMode}
              />
            )}
          </div>


          {/* Message Section */}
          <div className="space-y-2">
            <Label htmlFor="share-message" className="font-semibold dark:text-[#f0f6fc]">
              Share Message (Optional)
            </Label>
            <Textarea
              id="share-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add a message to include with the shared file..."
              rows={3}
              className="resize-none dark:bg-[#0d1117] dark:border-[#30363d] dark:text-[#f0f6fc] focus:ring-[#f0883e]"
            />
          </div>
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            className="dark:border-[#30363d] dark:text-[#c9d1d9] dark:hover:bg-[#30363d]"
          >
            Cancel
          </Button>
          <Button
            onClick={handleShare}
            variant="default"
            className="dark:bg-[#f0883e] dark:hover:bg-[#e0772d] dark:text-white"
            disabled={
              selectedFiles.length === 0 ||
              selectedRecipients.length === 0 ||
              shareFileMutation.isPending
            }
          >
            {shareFileMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sharing...
              </>
            ) : (
              "Share File"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
